import React, { useState } from 'react';

type Stage = 'Prospect'|'Qualified'|'Quoted'|'Negotiating'|'Closed Won'|'Closed Lost';
interface Deal { id:number; name:string; value:number; stage:Stage; company:string; agent:string; }

const STAGES: Stage[] = ['Prospect','Qualified','Quoted','Negotiating','Closed Won','Closed Lost'];
const STAGE_COLOR: Record<Stage,string> = {
  Prospect:'bg-gray-100 border-gray-300', Qualified:'bg-blue-50 border-blue-300',
  Quoted:'bg-yellow-50 border-yellow-300', Negotiating:'bg-orange-50 border-orange-300',
  'Closed Won':'bg-green-50 border-green-400', 'Closed Lost':'bg-red-50 border-red-300'
};

const INITIAL: Deal[] = [
  {id:1,name:'Rivera Family — Whole Life',value:3200,stage:'Qualified',company:'Individual',agent:'Sarah'},
  {id:2,name:'ABC Logistics — Commercial Auto',value:22140,stage:'Negotiating',company:'ABC Logistics Inc.',agent:'Mike'},
  {id:3,name:'Southeast Catering — GL',value:18750,stage:'Quoted',company:'Southeast Catering Group',agent:'Emma'},
  {id:4,name:'Johnson Renewal — Home',value:2100,stage:'Prospect',company:'Individual',agent:'Charlotte'},
  {id:5,name:'Davis Business — BOP',value:8400,stage:'Closed Won',company:'Davis LLC',agent:'Adam'},
  {id:6,name:'Park Family — Auto Bundle',value:4800,stage:'Qualified',company:'Individual',agent:'Sarah'},
];

const DealPipeline: React.FC = () => {
  const [deals, setDeals] = useState<Deal[]>(INITIAL);
  const [showModal, setShowModal] = useState(false);
  const [newDeal, setNewDeal] = useState({ name:'', value:'', company:'', stage:'Prospect' as Stage });

  const addDeal = () => {
    if (!newDeal.name) return;
    setDeals(p => [...p, { id: Date.now(), name: newDeal.name, value: Number(newDeal.value)||0, company: newDeal.company, stage: newDeal.stage, agent:'You' }]);
    setNewDeal({ name:'', value:'', company:'', stage:'Prospect' });
    setShowModal(false);
  };

  const moveStage = (id: number, direction: 1|-1) => {
    setDeals(prev => prev.map(d => {
      if (d.id !== id) return d;
      const idx = STAGES.indexOf(d.stage);
      const next = STAGES[Math.max(0, Math.min(STAGES.length-1, idx+direction))];
      return { ...d, stage: next };
    }));
  };

  const totalValue = deals.filter(d => d.stage === 'Closed Won').reduce((s,d) => s+d.value, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deal Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">Won: ${totalValue.toLocaleString()} · {deals.filter(d=>d.stage==='Closed Won').length} closed deals</p>
        </div>
        <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ New Deal</button>
      </div>

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {STAGES.map(stage => {
            const stageDeal = deals.filter(d => d.stage === stage);
            const stageValue = stageDeal.reduce((s,d)=>s+d.value,0);
            return (
              <div key={stage} className="w-64 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-600 uppercase">{stage}</span>
                  <span className="text-xs text-gray-500">{stageDeal.length} · ${stageValue.toLocaleString()}</span>
                </div>
                <div className="space-y-2 min-h-16">
                  {stageDeal.map(d => (
                    <div key={d.id} className={`p-3 rounded-lg border-2 ${STAGE_COLOR[stage]}`}>
                      <p className="text-sm font-semibold text-gray-900 truncate">{d.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{d.company}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm font-bold text-green-700">${d.value.toLocaleString()}</span>
                        <div className="flex gap-1">
                          {STAGES.indexOf(d.stage) > 0 && (
                            <button onClick={() => moveStage(d.id,-1)} className="w-5 h-5 bg-gray-200 rounded flex items-center justify-center text-gray-600 hover:bg-gray-300 text-xs">←</button>
                          )}
                          {STAGES.indexOf(d.stage) < STAGES.length-1 && (
                            <button onClick={() => moveStage(d.id,1)} className="w-5 h-5 bg-blue-200 rounded flex items-center justify-center text-blue-700 hover:bg-blue-300 text-xs">→</button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{d.agent}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-4">New Deal</h2>
            <div className="space-y-3">
              <input placeholder="Deal name" value={newDeal.name} onChange={e=>setNewDeal(p=>({...p,name:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input placeholder="Company / Client" value={newDeal.company} onChange={e=>setNewDeal(p=>({...p,company:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input type="number" placeholder="Policy value ($)" value={newDeal.value} onChange={e=>setNewDeal(p=>({...p,value:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <select value={newDeal.stage} onChange={e=>setNewDeal(p=>({...p,stage:e.target.value as Stage}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {STAGES.slice(0,-1).map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={addDeal} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Add Deal</button>
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default DealPipeline;
